import React from 'react';
import { Box, Flex, Heading, Notice, Text } from '@applyboard/crystal-ui';
import styled from '@emotion/styled';
import { App } from '../../clients';
import { AppAccessSwitch } from './AppAccessSwitch';
import { RolesAndAccessHeading } from '../FormLayout/RolesAndAccessHeading';

export interface AppConfig {
  appName: App;
  appLabel: string;
  logoSrc: string;
  value?: boolean;
}

interface AppAccessSectionProps {
  isDisabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Field: React.ComponentType<any>;
  apps: AppConfig[];
  noticeType?: 'profile' | 'agent';
  showBorder?: boolean;
}

export function AppAccessSection({
  isDisabled,
  Field,
  apps,
  noticeType = 'profile',
  showBorder = false,
}: AppAccessSectionProps): JSX.Element {
  return (
    <>
      <RolesAndAccessHeading />
      <RolesAndAccessInfoNotice noticeType={noticeType} />
      <Flex direction="column" justify="start" align="start" gap={4} pt={{ xs: 8, sm: 10 }}>
        <Heading level={2}>App Access</Heading>
        <AppContainer showBorder={showBorder}>
          {apps.map((app) => (
            <AppAccessSwitch
              key={app.appName}
              appName={app.appName}
              appLabel={app.appLabel}
              logoSrc={app.logoSrc}
              isDisabled={isDisabled}
              Field={Field}
              value={app.value}
              showBorder={showBorder}
            />
          ))}
        </AppContainer>
      </Flex>
    </>
  );
}

function RolesAndAccessInfoNotice({ noticeType }: { noticeType: 'profile' | 'agent' }): JSX.Element {
  if (noticeType === 'agent') {
    return (
      <Box pt={6} pb={12}>
        <Notice width="fill">
          <Box>
            <Text variant={'bodyS'}>
              <b>Agent Owner</b> has the highest level of access within the applications authorized for their agency
            </Text>
          </Box>
        </Notice>
      </Box>
    );
  }

  // Default: profile notice
  return (
    <Box pt={6} pb={12}>
      <Notice width="fill">
        <Box pb={2}>
          <Text>
            <b>Admin</b> users can add and remove users, adjust account-wide settings, and add additional apps to their
            account.
          </Text>
        </Box>
        <Box>
          <Text>
            <b>Staff</b> users can only view and update their personal profile information and security settings.
          </Text>
        </Box>
      </Notice>
    </Box>
  );
}

interface AppContainerProps {
  showBorder: boolean;
}

const AppContainer = styled.div<AppContainerProps>(({ showBorder }) => ({
  width: showBorder ? '100%' : '50%',
  ...(showBorder && {
    border: '1px solid #EAEEF7',
    padding: '8px 16px',
  }),
}));
